<template>
  <div>
    <div class="title">
      <gl-sub-text>出力形式</gl-sub-text>
    </div>
    <div>
      <md-radio
        :model="clusterMode"
        @change="$emit('update:clusterMode', $event)"
        value="item"
      >
        単品<gl-sub-text>でクラスター分析</gl-sub-text>
      </md-radio>
      <md-radio
        :model="clusterMode"
        @change="$emit('update:clusterMode', $event)"
        value="category"
      >
        分類<gl-sub-text>でクラスター分析</gl-sub-text>
      </md-radio>
    </div>

    <div class="title">
      <gl-sub-text>商品/分類指定</gl-sub-text>
    </div>

    <gl-select-company
      :company="company"
      @update:company="$emit('update:company', $event)"
    />

    <div v-if="clusterMode == 'item'">
      <div>
        <md-radio
          :model="selectMode"
          @change="$emit('update:selectMode', $event)"
          value="item"
        >
          商品
          <gl-sub-text>で指定</gl-sub-text>
        </md-radio>
        <md-radio
          :model="selectMode"
          @change="$emit('update:selectMode', $event)"
          value="category"
        >
          分類
          <gl-sub-text>で指定</gl-sub-text>
        </md-radio>
        <md-radio
          :model="selectMode"
          @change="$emit('update:selectMode', $event)"
          value="item-group-file"
        >
          <div>
            <a
              class="file-input-link"
              href="#"
              @click.prevent="$refs.file.click()"
            >
              階層ファイル
            </a>
            <span v-if="itemGroupFile">（{{ itemGroupFileName }}）</span>
            <gl-sub-text>で指定</gl-sub-text>
          </div>
        </md-radio>
        <input
          type="file"
          ref="file"
          accept=".txt,.csv"
          hidden
          @change="setItemGroupFile($event.target.files)"
        />
      </div>
      <filter-item
        v-if="company && selectMode === 'item'"
        :company="company"
        v-bind.sync="item"
      />
      <filter-item-category
        v-if="selectMode === 'category'"
        :company="company"
        v-bind.sync="item"
      />
    </div>

    <filter-category
      v-if="clusterMode == 'category'"
      :company="company"
      v-bind.sync="category"
    />

    <div class="radio-buttons">
      <md-radio
        :model="includeSiblings"
        @change="$emit('update:includeSiblings', $event)"
        :value="false"
        :disabled="disabledIncludeSiblingsValues.includes(false)"
      >
        指定の範囲内で分析
      </md-radio>
      <md-radio
        :model="includeSiblings"
        @change="$emit('update:includeSiblings', $event)"
        :value="true"
        :disabled="disabledIncludeSiblingsValues.includes(true)"
      >
        <div class="input-inline input-inline--within-radio">
          <label class="input-inline__label">指定の所属</label>
          <md-field class="input-inline__field">
            <md-select
              :value="itemCategoryLevel"
              :disabled="disabledIncludeSiblingsValues.includes(true)"
              @md-selected="$emit('update:itemCategoryLevel', $event)"
            >
              <md-option
                v-for="level in itemCategoryLevels"
                :key="level.level"
                :value="level.level"
              >
                {{ level.name }}
              </md-option>
            </md-select>
          </md-field>
          <label class="input-inline__label">配下の商品も分析に含める</label>
        </div>
      </md-radio>
    </div>
    <div>
      <gl-switch
        :value="excludeNonActive"
        @input="$emit('update:excludeNonActive', $event)"
        :disabled="isExcludeNonActiveDisabled"
      >
        実績がない商品/分類を結果から除外する
      </gl-switch>
    </div>
  </div>
</template>

<script>
import ErrorReport from '@/mixins/ErrorReport'

import FilterItem from '@/components/FilterItem.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterCategory from '@/components/FilterCategory.vue'

export default {
  mixins: [ErrorReport],
  components: {
    FilterItem,
    FilterItemCategory,
    FilterCategory,
  },
  props: {
    clusterMode: {
      type: String,
      validate(v) {
        return ['item', 'category'].includes(v)
      },
    },
    company: {
      type: String,
    },
    selectMode: {
      type: String,
      validate(v) {
        return ['item', 'category'].includes(v)
      },
    },
    itemGroupFile: {
      type: File,
    },
    includeSiblings: {
      type: Boolean,
      required: true,
    },
    excludeNonActive: {
      type: Boolean,
      required: true,
    },
    itemCategoryLevel: {
      type: Number,
    },
    item: {
      items: {
        type: Array,
        default: () => [],
      },
      categories: {
        type: Array,
        default: () => [],
      },
    },
    category: {
      itemCategoryLevel: {
        type: Number,
      },
      categories: {
        type: Array,
        default: () => [],
      },
    },
  },
  data() {
    return {
      itemCategoryLevels: [],
    }
  },
  computed: {
    isExcludeNonActiveDisabled() {
      // 単品の明示的な指定では実績0を含める
      if (this.clusterMode === 'item') {
        if (this.selectMode === 'item') {
          if (!this.includeSiblings) {
            return true
          }
        }
      }
      return false
    },
    disabledIncludeSiblingsValues() {
      // 分類で選択（出力が単品・分類クラスターどちらでも）している場合は
      // 「配下を分析に含める」ことはできない。
      if (this.clusterMode === 'category') {
        return [true]
      }
      if (this.selectMode === 'category') {
        return [true]
      }
      if (this.selectMode === 'item-group-file') {
        return [false]
      }

      return []
    },
    itemGroupFileName() {
      return this.itemGroupFile.name
    },
    errors() {
      const errors = []

      if (this.clusterMode === 'item') {
        if (this.selectMode === 'item' && this.item.items.length === 0) {
          errors.push('商品を選択してください。')
        }

        if (
          this.selectMode === 'category' &&
          this.item.categories.length === 0
        ) {
          errors.push('分類を選択してください。')
        }

        if (this.selectMode === 'item-group-file' && !this.itemGroupFile) {
          errors.push('階層ファイルを選択してください。')
        }
      }

      if (this.clusterMode === 'category') {
        if (this.category.categories.length === 0) {
          errors.push('分類を選択してください。')
        }
      }

      return errors
    },
  },
  watch: {
    clusterMode(clusterMode) {
      if (clusterMode === 'item') {
        this.$emit('update:selectMode', 'item')
      } else if (clusterMode === 'category') {
        this.$emit('update:selectMode', 'category')
      }
    },
    selectMode(selectMode) {
      if (selectMode === 'item') {
        this.$emit('update:includeSiblings', false)
        this.$emit('update:excludeNonActive', false)
      } else if (selectMode === 'category') {
        this.$emit('update:includeSiblings', false)
        this.$emit('update:excludeNonActive', true)
      } else if (selectMode === 'item-group-file') {
        this.$emit('update:includeSiblings', true)
      }
    },
    includeSiblings(includeSiblings) {
      if (includeSiblings) {
        this.$emit('update:excludeNonActive', true)
      } else {
        this.$emit('update:excludeNonActive', false)
      }
    },
    company: {
      immediate: true,
      handler(company) {
        if (!company) {
          return
        }
        this.itemCategoryLevels = this.$store.state.itemCategoryLevels[company]

        if (!this.itemCategoryLevel) {
          // 必ずどこかの階層選択は必要なので、指定されていない場合は最下層とする
          this.$emit(
            'update:itemCategoryLevel',
            this.itemCategoryLevels[this.itemCategoryLevels.length - 1].level,
          )
        }
      },
    },
  },
  methods: {
    setItemGroupFile(files) {
      if (files.length === 0) {
        // ファイル選択ダイアログは開いたが、キャンセルした時は
        // 選択ファイルを変更しない。
        return
      }
      this.$emit('update:itemGroupFile', files[0])
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  margin-top: var(--spacing-medium);
}
</style>
